/**
 * Persistent per-user cache of "redemption readiness" flags.
 *
 * Tracks three boolean milestones for each user:
 *   - profileComplete       : all required profile fields are filled
 *   - hasPaymentAccount     : the user has at least one saved payment account
 *                             appropriate for their country (JazzCash for PK,
 *                             PayPal otherwise)
 *   - paymentTermsAccepted  : the user has accepted the Payment Terms &
 *                             Liability Agreement
 *
 * Once a milestone flips to `true` it persists across sessions and the UI
 * can skip the network call to re-derive it. This lets the user log in,
 * pre-fetch their state once, and then load `My Summary` / `Redemptions`
 * with zero further requests.
 *
 * Storage: localStorage, keyed per `userId` and per API base URL so
 * switching local ↔ hosted backends does not reuse stale flags.
 */

import { API_BASE_URL } from '../config/apiConfig';

function apiBaseStorageTag() {
  const u = String(API_BASE_URL || '');
  let h = 0;
  for (let i = 0; i < u.length; i += 1) {
    h = (Math.imul(31, h) + u.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

const STORAGE_PREFIX = `dashboardRedemptionStatus_v1_${apiBaseStorageTag()}_`;

const FLAG_KEYS = ['profileComplete', 'hasPaymentAccount', 'paymentTermsAccepted'];

function storageKey(userId) {
  if (userId == null || !Number.isFinite(Number(userId))) return null;
  return STORAGE_PREFIX + Number(userId);
}

function emptyStatus(userId) {
  return {
    userId: Number(userId) || null,
    t: 0,
    profileComplete: false,
    hasPaymentAccount: false,
    paymentTermsAccepted: false,
  };
}

export function readUserRedemptionStatusCache(userId) {
  const key = storageKey(userId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      userId: Number(parsed.userId) || Number(userId),
      t: Number(parsed.t) || 0,
      profileComplete: Boolean(parsed.profileComplete),
      hasPaymentAccount: Boolean(parsed.hasPaymentAccount),
      paymentTermsAccepted: Boolean(parsed.paymentTermsAccepted),
    };
  } catch {
    return null;
  }
}

/**
 * Merge a partial status update. By default flags can only flip `false → true`
 * (so a transient API hiccup never clears a real completion). Pass
 * `{ force: true }` to allow flipping a flag back to false explicitly
 * (e.g. when the user edits their profile and removes a required field).
 */
export function mergeUserRedemptionStatusCache(userId, partial, options = {}) {
  const key = storageKey(userId);
  if (!key || !partial || typeof partial !== 'object') return;
  const previous = readUserRedemptionStatusCache(userId) || emptyStatus(userId);
  const next = { ...previous, userId: Number(userId), t: Date.now() };
  for (const flag of FLAG_KEYS) {
    if (!(flag in partial)) continue;
    const nextValue = Boolean(partial[flag]);
    if (options.force) {
      next[flag] = nextValue;
    } else if (nextValue) {
      next[flag] = true;
    }
  }
  try {
    localStorage.setItem(key, JSON.stringify(next));
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(
        new CustomEvent('dreamy:redemptionStatusUpdated', {
          detail: { userId: next.userId, status: next },
        })
      );
    }
  } catch {
    /* quota / private mode */
  }
}

export function clearUserRedemptionStatusCache(userId = null) {
  try {
    if (userId != null && Number.isFinite(Number(userId))) {
      localStorage.removeItem(STORAGE_PREFIX + Number(userId));
      return;
    }
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

export function isRedemptionFullyComplete(userId) {
  const s = readUserRedemptionStatusCache(userId);
  if (!s) return false;
  return Boolean(s.profileComplete && s.hasPaymentAccount && s.paymentTermsAccepted);
}

/** Same profile required-fields rule used by WithdrawHub.bootstrap. */
export function evaluateProfileCompleteFromUser(user) {
  if (!user || typeof user !== 'object') return false;
  const cc = String(
    user.CountryCode || user.countryCode || user.Country || user.country || ''
  ).toUpperCase();
  const isPK = cc === 'PK';
  const isProvided = (keys) => {
    for (const k of keys) {
      const v = user?.[k];
      if (v !== undefined && v !== null && String(v).trim() !== '') return true;
    }
    return false;
  };
  const checks = {
    SexId: ['SexId', 'SexID', 'sexId', 'GenderId', 'GenderID', 'genderId'],
    Email: ['Email', 'email'],
    CountryOfOriginId: [
      'CountryOfOriginId',
      'CountryOfOriginID',
      'countryOfOriginId',
      'CountryOfOrigin',
    ],
    CountryOfResidenceId: [
      'CountryOfResidenceId',
      'CountryOfResidenceID',
      'countryOfResidenceId',
      'CountryOfResidence',
    ],
    EthnicityId: ['EthnicityId', 'EthnicityID', 'ethnicityId'],
    DateOfBirth: ['DateOfBirth', 'dateOfBirth', 'DOB', 'BirthDate', 'birthDate'],
    AddressLine1: ['AddressLine1', 'addressLine1', 'Address', 'address', 'Address1'],
    City: ['City', 'city', 'Town'],
  };
  if (!isPK) {
    checks.PostalCode = ['PostalCode', 'postalCode', 'PostCode', 'Zip', 'ZipCode'];
  }
  for (const keys of Object.values(checks)) {
    if (!isProvided(keys)) return false;
  }
  return true;
}

/**
 * Whether the user has saved the payment method that maps to redemptions in
 * their country. PK → JazzCash, everyone else → PayPal. We treat the mere
 * presence of the account row as proof of completion: the backend may not
 * echo sensitive fields (e.g. CNIC) back in subsequent fetches.
 */
export function evaluateHasPaymentAccountFromAccounts(accounts, countryCode) {
  if (!Array.isArray(accounts) || accounts.length === 0) return false;
  const cc = String(countryCode || '').toUpperCase();
  const matchName = cc === 'PK' ? 'JazzCash' : 'PayPal';
  return accounts.some(
    (a) => (a?.PaymentMethodName || a?.Provider) === matchName
  );
}

export function evaluateTermsAcceptedFromPayloads(profileUser, paymentSettings) {
  const fromProfile =
    profileUser?.PaymentTermsAccepted === true ||
    profileUser?.PaymentTermsAccepted === 1 ||
    profileUser?.paymentTermsAccepted === true;
  if (fromProfile) return true;
  return (
    paymentSettings?.PaymentTermsAccepted === true ||
    paymentSettings?.PaymentTermsAccepted === 1
  );
}
